// src/store/slices/equivalentFractionsSlice.ts
/**
 * Equivalent Fractions view mode state with localStorage-backed defaults.
 * Persists the preferred list of denominators, while keeping a
 * session-only active list that can be edited without changing preferences.
 */

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { EquivalentFractionsState, ViewMode, RootState, ViewportState } from '../../types/redux';
import { centerOrigin, loadViewportState, setScale } from './viewportSlice';

const STORAGE_KEY = 'nl:equivalentFractions';
const MAX_DENOMINATORS = 12;
const DEFAULT_DENOMINATORS = [2, 3, 4, 6, 12] as const;

// Define default viewport states
const DEFAULT_STANDARD_VIEWPORT: Partial<ViewportState> = {
  scale: 83,
  offsetX: 400,
  offsetY: 200,
  orientation: 'horizontal',
  showNegatives: true,
};

const DEFAULT_EQUIV_VIEWPORT: Partial<ViewportState> = {
  scale: 697, // User requested 697px default for equivalent fractions
  offsetX: 400,
  offsetY: 200,
  orientation: 'horizontal',
  showNegatives: true,
};

const defaultState: EquivalentFractionsState = {
  mode: 'standard',
  denominators: [...DEFAULT_DENOMINATORS],
  activeDenominators: [...DEFAULT_DENOMINATORS],
  integerLabelsOnly: true,
  labelFormat: 'fraction',
  savedViewports: {
    standard: DEFAULT_STANDARD_VIEWPORT as ViewportState,
    equivalentFractions: DEFAULT_EQUIV_VIEWPORT as ViewportState,
  }
};

function normalizeDenominators(input: number[] | undefined, fallback = DEFAULT_DENOMINATORS): number[] {
  if (!Array.isArray(input)) return [...fallback];
  const uniqueSorted = Array.from(
    new Set(
      input
        .map((d) => Math.abs(Math.trunc(d)))
        .filter((d) => Number.isFinite(d) && d > 0)
    )
  )
    .sort((a, b) => a - b)
    .slice(0, MAX_DENOMINATORS);
  return uniqueSorted.length > 0 ? uniqueSorted : [...fallback];
}

function loadFromStorage(): EquivalentFractionsState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    const persistedDenominators = normalizeDenominators(parsed.denominators);
    
    // Ensure savedViewports exists, merging with defaults
    const savedViewports = {
      standard: { ...DEFAULT_STANDARD_VIEWPORT, ...(parsed.savedViewports?.standard || {}) },
      equivalentFractions: { ...DEFAULT_EQUIV_VIEWPORT, ...(parsed.savedViewports?.equivalentFractions || {}) },
      coordinateGrid: parsed.savedViewports?.coordinateGrid,
    };

    const state: EquivalentFractionsState = {
      mode: parsed.mode === 'equivalentFractions' 
        ? 'equivalentFractions' 
        : parsed.mode === 'coordinateGrid'
          ? 'coordinateGrid'
          : 'standard',
      denominators: persistedDenominators,
      activeDenominators: normalizeDenominators(parsed.activeDenominators, persistedDenominators),
      integerLabelsOnly: parsed.integerLabelsOnly !== false,
      labelFormat: parsed.labelFormat === 'decimal' ? 'decimal' : 'fraction',
      savedViewports: savedViewports as any,
    };
    return state;
  } catch (error) {
    console.warn('Failed to load equivalent fractions state from storage', error);
    return defaultState;
  }
}

function saveToStorage(state: EquivalentFractionsState) {
  try {
    const { mode, denominators, integerLabelsOnly, labelFormat, savedViewports } = state;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        mode,
        denominators,
        integerLabelsOnly,
        labelFormat,
        savedViewports,
      })
    );
  } catch (error) {
    console.warn('Failed to save equivalent fractions state', error);
  }
}

const initialState: EquivalentFractionsState = (() => {
  const loaded = loadFromStorage();
  const activeDenominators = normalizeDenominators(loaded.activeDenominators, loaded.denominators);
  return {
    ...loaded,
    activeDenominators,
  };
})();

export const equivalentFractionsSlice = createSlice({
  name: 'equivalentFractions',
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<ViewMode>) => {
      state.mode = action.payload;
      saveToStorage(state);
    },
    setDenominators: (state, action: PayloadAction<number[]>) => {
      const sanitized = normalizeDenominators(action.payload);
      state.denominators = sanitized;
      state.activeDenominators = sanitized;
      saveToStorage(state);
    },
    addDenominator: (state, action: PayloadAction<number>) => {
      const candidate = Math.abs(Math.trunc(action.payload));
      if (!candidate || !Number.isFinite(candidate)) return;
      const next = normalizeDenominators([...state.activeDenominators, candidate], state.denominators);
      state.activeDenominators = next;
    },
    removeDenominator: (state, action: PayloadAction<number>) => {
      const remaining = state.activeDenominators.filter((d) => d !== action.payload);
      state.activeDenominators = remaining.length > 0 ? remaining : [...state.denominators];
    },
    resetActiveDenominators: (state) => {
      state.activeDenominators = [...state.denominators];
    },
    setIntegerLabelsOnly: (state, action: PayloadAction<boolean>) => {
      state.integerLabelsOnly = action.payload;
      saveToStorage(state);
    },
    setLabelFormat: (state, action: PayloadAction<'fraction' | 'decimal'>) => {
      state.labelFormat = action.payload;
      saveToStorage(state);
    },
    loadEquivalentFractionsState: (state, action: PayloadAction<EquivalentFractionsState>) => {
      const sanitizedBase = normalizeDenominators(action.payload.denominators);
      const sanitizedActive = normalizeDenominators(
        action.payload.activeDenominators ?? action.payload.denominators,
        sanitizedBase
      );
      state.mode = action.payload.mode;
      state.denominators = sanitizedBase;
      state.activeDenominators = sanitizedActive;
      state.integerLabelsOnly = action.payload.integerLabelsOnly;
      state.labelFormat = action.payload.labelFormat ?? 'fraction';
      state.savedViewports = action.payload.savedViewports || state.savedViewports;
      saveToStorage(state);
    },
    saveViewportState: (state, action: PayloadAction<{ mode: ViewMode; viewport: ViewportState }>) => {
      const { mode, viewport } = action.payload;
      if (!state.savedViewports) state.savedViewports = {};
      state.savedViewports[mode] = viewport;
      saveToStorage(state);
    },
  },
});

export const {
  setMode,
  setDenominators,
  addDenominator,
  removeDenominator,
  resetActiveDenominators,
  setIntegerLabelsOnly,
  setLabelFormat,
  loadEquivalentFractionsState,
  saveViewportState,
} = equivalentFractionsSlice.actions;

/**
 * Async thunk to set view mode with coordinate grid initialization
 * When switching to coordinate grid mode, centers the origin in the viewport
 */
export const setViewMode = createAsyncThunk<
  ViewMode,
  { mode: ViewMode; viewportWidth?: number; viewportHeight?: number },
  { state: RootState }
>(
  'equivalentFractions/setViewMode',
  async ({ mode: targetMode, viewportWidth, viewportHeight }, { dispatch, getState }) => {
    const state = getState();
    const currentMode = state.equivalentFractions.mode;
    const currentViewport = state.viewport;

    // 1. Save current viewport for the mode we are leaving
    // Only save if we have meaningful data (e.g. valid scale)
    if (currentViewport && currentViewport.scale > 0) {
      dispatch(saveViewportState({ mode: currentMode, viewport: currentViewport }));
    }

    // 2. Set the new mode
    dispatch(setMode(targetMode));
    
    // 3. Handle Viewport Restoration or Initialization
    
    // Get saved viewport for the target mode
    // Note: We need to check state.equivalentFractions.savedViewports but getting the updated state requires
    // waiting for the reducer or assuming the logic. Since reducers run synchronously, we can rely on 
    // savedViewports being updated in the next render cycle, OR we can just check what we have in `state` 
    // (which is pre-dispatch of saveViewportState) + what we just saved.
    // However, Redux state inside thunk is from getState() at start. We can call getState() again.
    
    // We already dispatched saveViewportState, but that updates the store. 
    // We need fresh state to see if there is a saved viewport for the target mode.
    const updatedState = getState();
    const savedViewport = updatedState.equivalentFractions.savedViewports?.[targetMode];

    if (targetMode === 'coordinateGrid') {
      // Coordinate Grid special handling: always center origin initially
      // unless we want to restore previous grid state?
      // The previous logic was: center origin.
      // If we want to support restoring coordinate grid state, we would use savedViewport.
      // But typically coordinate grid might need specific initialization.
      // For now, let's preserve existing behavior for coordinate grid unless user asks otherwise.
      // Actually, user said "standard line" and "equivalent fractions". 
      // Coordinate grid wasn't explicitly mentioned to have memory, but consistency is good.
      
      // Original logic:
      const width = viewportWidth ?? 800;
      const height = viewportHeight ?? 400;
      dispatch(centerOrigin({ width, height }));
      
    } else {
      // Standard or Equivalent Fractions
      if (savedViewport) {
        // Restore saved state
        dispatch(loadViewportState(savedViewport));
      } else {
        // Fallback defaults (should be covered by initialState, but just in case)
        if (targetMode === 'equivalentFractions') {
           dispatch(setScale(697)); // User requested default
           // Maybe ensure reasonable offset?
           dispatch(centerOrigin({ width: viewportWidth ?? 800, height: viewportHeight ?? 400 }));
        } else {
           dispatch(setScale(83));
           dispatch(centerOrigin({ width: viewportWidth ?? 800, height: viewportHeight ?? 400 }));
        }
      }
    }
    
    return targetMode;
  }
);

export default equivalentFractionsSlice.reducer;
